import { useState, useMemo } from 'react'
import GameStatusBadge from './GameStatusBadge'
import { useReceptionsPlayers, useReceptionsPicks, useReceptionsUsed, useSubmitReceptionsPicks, useReceptionsStandings } from '../../hooks/useLeagues'
import { useAuth } from '../../hooks/useAuth'
import { toast } from '../ui/Toast'
import LoadingSpinner from '../ui/LoadingSpinner'
import Avatar from '../ui/Avatar'
import PlayerDetailModal from '../ui/PlayerDetailModal'
import UserProfileModal from '../profile/UserProfileModal'
import PlayerHeadshot from '../ui/PlayerHeadshot'

const INJURY_COLORS = {
  Out: 'text-incorrect',
  IR: 'text-incorrect',
  Questionable: 'text-yellow-400',
  Doubtful: 'text-yellow-400',
  Probable: 'text-correct',
  'Day-To-Day': 'text-yellow-400',
}

function InjuryBadge({ status }) {
  if (!status) return null
  const label = status === 'Day-To-Day' ? 'DTD' : status === 'IR' ? 'IR' : status.charAt(0)
  return (
    <span className={`text-[12px] font-mono font-bold shrink-0 ${INJURY_COLORS[status] || 'text-text-muted'}`} title={status}>
      {label}
    </span>
  )
}

function receptionLabel(n) {
  return Number(n) === 1 ? 'reception' : 'receptions'
}

export default function ReceptionsView({ league, tab = 'picks' }) {
  const { profile } = useAuth()

  const { data: poolData, isLoading: playersLoading } = useReceptionsPlayers()
  const { data: myPicks, isLoading: picksLoading } = useReceptionsPicks(league.id)
  const { data: usedPlayers } = useReceptionsUsed(league.id)
  const submitPicks = useSubmitReceptionsPicks()
  const { data: standingsData } = useReceptionsStandings(league.id)

  const players = poolData?.players || []
  const week = poolData?.week
  const season = poolData?.season

  const [selected, setSelected] = useState([])
  const [search, setSearch] = useState('')
  const [standingsUserId, setStandingsUserId] = useState(null)
  const [profileUserId, setProfileUserId] = useState(null)
  const [initialized, setInitialized] = useState(false)
  const [editing, setEditing] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [detailPlayer, setDetailPlayer] = useState(null)

  if (!initialized && myPicks?.length && players?.length && !selected.length) {
    const loaded = myPicks.map((pick) =>
      // Spread the pick itself rather than naming fields: /my-picks now
      // serves injury_status, opponent and kickoff, and a hand-listed
      // fallback silently dropped them. A player picked while healthy and
      // later ruled Out is no longer in the pool, so this branch is exactly
      // the case that most needs its injury badge.
      players.find((p) => p.sleeper_player_id === pick.sleeper_player_id) || { ...pick }
    ).filter(Boolean)
    if (loaded.length) {
      setSelected(loaded)
      setInitialized(true)
    }
  }

  const hasSavedPicks = myPicks?.length > 0
  const nowMs = Date.now()
  const isPickLocked = (p) => p?.game_starts_at && new Date(p.game_starts_at).getTime() <= nowMs
  const allPicksLocked = hasSavedPicks && (myPicks || []).every(isPickLocked)
  const usedPlayerIds = new Set((usedPlayers || []).map((u) => u.sleeper_player_id))
  const usageById = new Map((usedPlayers || []).map((u) => [u.sleeper_player_id, u]))
  const thisWeekPickIds = new Set((myPicks || []).map((p) => p.sleeper_player_id))
  const selectedIds = new Set(selected.map((p) => p.sleeper_player_id))

  const myHistory = useMemo(() => {
    if (!standingsData?.standings || !profile?.id) return []
    const me = standingsData.standings.find((s) => s.user?.id === profile.id)
    if (!me?.picks?.length) return []
    const byWeek = {}
    for (const p of me.picks) {
      if (!byWeek[p.week]) byWeek[p.week] = []
      byWeek[p.week].push(p)
    }
    return Object.entries(byWeek)
      .sort(([a], [b]) => Number(b) - Number(a))
      .map(([w, picks]) => ({ week: Number(w), picks }))
  }, [standingsData, profile?.id])

  const filteredPlayers = useMemo(() => {
    if (!players) return []
    return players.filter((p) => {
      if (selectedIds.has(p.sleeper_player_id)) return false
      if (p.injury_status === 'Out') return false
      if (search) {
        const q = search.toLowerCase()
        if (!p.player_name.toLowerCase().includes(q) && !(p.team || '').toLowerCase().includes(q)) return false
      }
      return true
    })
  }, [players, search, selectedIds])

  function addPlayer(player) {
    if (player.is_locked) {
      // Says what's actually true. Locked players are shown now rather than
      // filtered out, so the reason has to be legible: his game already
      // kicked off, not "he's unavailable".
      toast(`${player.player_name}'s game has already started`, 'error')
      return
    }
    if (selected.length >= 3) {
      toast('Maximum 3 picks per week', 'error')
      return
    }
    setSelected((prev) => [...prev, player])
  }

  function removePlayer(playerId) {
    setSelected((prev) => prev.filter((p) => p.sleeper_player_id !== playerId))
  }

  async function handleSubmit() {
    if (selected.length !== 3) return
    try {
      await submitPicks.mutateAsync({
        league_id: league.id,
        players: selected.map((p) => ({
          sleeper_player_id: p.sleeper_player_id,
          player_name: p.player_name,
          position: p.position,
          team: p.team,
          headshot_url: p.headshot_url,
        })),
      })
      toast('Picks submitted!', 'success')
      setEditing(false)
    } catch (err) {
      toast(err.message || 'Failed to submit picks', 'error')
    }
  }

  if (tab === 'standings') {
    const standings = standingsData?.standings || []
    return (
      <div>
        {!standings.length ? (
          <div className="text-center py-8 text-sm text-text-secondary">No results yet.</div>
        ) : (
          <div className="rounded-2xl border border-text-primary/15 bg-bg-primary/15 backdrop-blur-md overflow-hidden">
            <div className="grid grid-cols-[1.5rem_1fr_3rem] lg:grid-cols-[2rem_1fr_3.5rem] gap-1.5 lg:gap-3 px-3 lg:px-5 py-3 border-b border-text-primary/10 text-xs text-text-muted uppercase tracking-wider">
              <span>#</span>
              <span>Player</span>
              <span className="text-right">Recs</span>
            </div>
            {standings.map((s) => {
              const isMe = s.user?.id === profile?.id
              const isExpanded = standingsUserId === s.user?.id
              return (
                <div key={s.user?.id} className="border-b border-text-primary/10 last:border-b-0">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setStandingsUserId(isExpanded ? null : s.user?.id)}
                    className={`w-full grid grid-cols-[1.5rem_1fr_3rem] lg:grid-cols-[2rem_1fr_3.5rem] gap-1.5 lg:gap-3 px-3 lg:px-5 py-3.5 lg:py-4 items-center text-left hover:bg-text-primary/5 transition-colors cursor-pointer ${isMe ? 'bg-accent/5' : ''}`}
                  >
                    <span className={`font-display text-lg lg:text-xl ${s.rank <= 3 ? 'text-accent' : 'text-text-muted'}`}>{s.rank}</span>
                    <div className="flex items-center gap-2 lg:gap-3 min-w-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); setProfileUserId(s.user?.id) }}
                        className="shrink-0"
                      >
                        <Avatar user={s.user} size="md" className="lg:!w-10 lg:!h-10" />
                      </button>
                      <span className={`font-bold truncate text-sm lg:text-base ${isMe ? 'text-accent' : 'text-text-primary'}`}>
                        {s.user?.display_name || s.user?.username}
                      </span>
                      <svg className={`w-4 h-4 text-accent shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </div>
                    <span className="font-display text-lg lg:text-xl text-white text-right">{s.totalReceptions}</span>
                  </div>
                  {isExpanded && (() => {
                    const thisWeekPicks = (s.picks || []).filter((p) => p.week === week)
                    const lastWeekPicks = week ? (s.picks || []).filter((p) => p.week === week - 1) : []
                    return (
                      <div className="px-3 lg:px-5 pb-3 space-y-3">
                        <div>
                          <div className="text-[10px] text-text-muted uppercase tracking-wider mb-2">Week {week}</div>
                          {!thisWeekPicks.length ? (
                            <p className="text-xs text-text-muted text-center py-2">No picks for week {week}</p>
                          ) : (
                            <div className="space-y-1.5">
                              {thisWeekPicks.map((pick, i) => pick.hidden ? (
                                <div key={i} className="flex items-center gap-2 lg:gap-3 bg-bg-primary/5 border border-text-primary/10 border-dashed rounded-lg px-2.5 lg:px-4 py-2 lg:py-3">
                                  <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-full bg-bg-secondary/40 shrink-0 flex items-center justify-center text-text-muted">
                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs lg:text-sm italic text-text-muted">Hidden until kickoff</div>
                                  </div>
                                  <span className="font-display text-sm lg:text-base shrink-0 text-text-muted">—</span>
                                </div>
                              ) : (
                                <div key={i} className="flex items-center gap-2 lg:gap-3 bg-bg-primary/10 border border-text-primary/10 rounded-lg px-2.5 lg:px-4 py-2 lg:py-3">
                                  <PlayerHeadshot name={pick.player_name} url={pick.headshot_url} size="sm" className="lg:w-10 lg:h-10" />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs lg:text-sm font-bold text-text-primary truncate">{pick.player_name}</div>
                                    <div className="text-[10px] lg:text-xs text-text-muted truncate">{pick.position} · {pick.team}</div>
                                  </div>
                                  <span className={`font-display text-sm lg:text-base shrink-0 ${pick.receptions > 0 ? 'text-correct' : 'text-text-muted'}`}>{pick.receptions} {receptionLabel(pick.receptions)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {lastWeekPicks.length > 0 && (
                          <div>
                            <div className="text-[10px] text-text-muted uppercase tracking-wider mb-2">Week {week - 1}</div>
                            <div className="space-y-1.5">
                              {lastWeekPicks.map((pick, i) => (
                                <div key={i} className="flex items-center gap-2 lg:gap-3 bg-bg-primary/10 border border-text-primary/10 rounded-lg px-2.5 lg:px-4 py-2 lg:py-3">
                                  <PlayerHeadshot name={pick.player_name} url={pick.headshot_url} size="sm" className="lg:w-10 lg:h-10" />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs lg:text-sm font-bold text-text-primary truncate">{pick.player_name}</div>
                                    <div className="text-[10px] lg:text-xs text-text-muted truncate">{pick.position} · {pick.team}</div>
                                  </div>
                                  <span className={`font-display text-sm lg:text-base shrink-0 ${pick.receptions > 0 ? 'text-correct' : 'text-text-muted'}`}>{pick.receptions} {receptionLabel(pick.receptions)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })()}
                </div>
              )
            })}
          </div>
        )}
        {profileUserId && <UserProfileModal userId={profileUserId} onClose={() => setProfileUserId(null)} />}
      </div>
    )
  }

  if (playersLoading || picksLoading) return <LoadingSpinner />

  return (
    <div className="flex flex-col lg:grid lg:grid-cols-2 lg:gap-6 pb-24 lg:pb-0">
      <div className="order-1 lg:col-start-1 lg:row-start-1">
        {week && (
          <div className="mb-3 text-xs uppercase tracking-wider text-text-muted">
            Week {week}{season ? ` · ${season}` : ''}
          </div>
        )}

        <div className="rounded-xl border border-text-primary/15 bg-bg-primary/10 backdrop-blur-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">This Week's Picks</h3>
            {!allPicksLocked && (
              <span className="text-xs text-text-muted">{selected.length}/3 picks</span>
            )}
          </div>

          {selected.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-4">Pick 3 pass catchers who you think will rack up receptions this week</p>
          ) : (
            <div className="space-y-2">
              {selected.map((player) => {
                const savedPick = (myPicks || []).find((p) => p.sleeper_player_id === player.sleeper_player_id)
                const receptions = Number(savedPick?.receptions) || 0
                return (
                  <div
                    key={player.sleeper_player_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setDetailPlayer(player)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setDetailPlayer(player) }}
                    className="flex items-center gap-2 bg-bg-primary/10 border border-text-primary/15 rounded-lg px-3 py-2.5 cursor-pointer hover:bg-text-primary/5 transition-colors"
                  >
                    <PlayerHeadshot name={player.player_name} url={player.headshot_url} size="md" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-sm font-bold text-text-primary truncate">{player.player_name}</span>
                        <InjuryBadge status={player.injury_status} />
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-xs text-text-muted truncate">
                          {player.position} · <span className="text-white">{player.team}</span>{player.opponent ? ` ${player.home_away === 'home' ? 'vs' : '@'} ${player.opponent}` : ''}
                        </span>
                        <GameStatusBadge
                          gameStartsAt={player.game_starts_at}
                          gameStatus={player.game_status}
                          teamScore={player.team_score}
                          oppScore={player.opp_score}
                          period={player.period}
                          clock={player.clock}
                        />
                      </div>
                    </div>
                    {hasSavedPicks && !editing && (
                      <span className={`font-display text-lg shrink-0 ${receptions > 0 ? 'text-correct' : 'text-text-muted'}`}>{receptions}</span>
                    )}
                    {(!hasSavedPicks || editing) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); removePlayer(player.sleeper_player_id) }}
                        className="p-2 text-text-muted hover:text-incorrect transition-colors text-lg leading-none"
                      >
                        &times;
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {hasSavedPicks && !editing && (() => {
          const weekReceptions = (myPicks || []).reduce((sum, p) => sum + (Number(p.receptions) || 0), 0)
          return (
            <div className="flex items-center justify-end gap-4 pr-7 mb-2 -mt-1">
              <span className="text-sm text-text-muted uppercase tracking-wider font-semibold">This Week</span>
              <span className="font-display flex items-baseline gap-1">
                <span className={`text-lg ${weekReceptions > 0 ? 'text-correct' : 'text-text-muted'}`}>{weekReceptions}</span>
                <span className="text-sm text-white">{receptionLabel(weekReceptions)}</span>
              </span>
            </div>
          )
        })()}

        {hasSavedPicks && !editing ? (
          allPicksLocked ? (
            /* Every pick's game has kicked off, so there is nothing left to
               change. Edit used to open normally and show a remove X on each
               locked player, then fail only on save with "<player>'s game has
               already started" — a dead end you could only find by trying.
               Say it up front instead. */
            <div className="w-full py-3 rounded-xl font-display border-2 border-text-primary/15 text-text-primary/70 text-center mb-6">
              Picks locked — all games started
            </div>
          ) : (
            <button
              onClick={() => setEditing(true)}
              className="w-full py-3 rounded-xl font-display border-2 border-accent text-accent bg-accent/5 hover:bg-accent/10 transition-colors mb-6"
            >
              Edit Picks
            </button>
          )
        ) : (
          <button
            onClick={handleSubmit}
            disabled={selected.length !== 3 || submitPicks.isPending}
            className="w-full py-3 rounded-xl font-display bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-6"
          >
            {submitPicks.isPending ? 'Saving...' : hasSavedPicks ? 'Save Picks' : 'Submit Picks'}
          </button>
        )}

        {usedPlayers?.filter((u) => u.week !== week).length > 0 && (
          <div className="mb-4">
            <div className="text-xs text-text-muted uppercase tracking-wider mb-2">Used This Season</div>
            <div className="flex flex-wrap gap-1.5">
              {usedPlayers.filter((u) => u.week !== week).map((u) => (
                <span key={u.sleeper_player_id} className="text-[10px] bg-bg-primary/30 border border-text-primary/10 text-text-muted px-2 py-1 rounded-full">
                  {u.player_name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="order-2 lg:col-start-2 lg:row-start-1 lg:row-span-2 rounded-xl border border-text-primary/15 bg-bg-primary/15 backdrop-blur-md overflow-hidden lg:max-h-[calc(100vh-200px)] lg:overflow-y-auto lg:sticky lg:top-4">
        <div className="px-4 py-3 border-b border-text-primary/10">
          <h3 className="text-sm font-semibold text-text-primary mb-3">Available Pass Catchers</h3>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search players..."
            className="w-full bg-bg-primary/15 border border-text-primary/15 rounded-lg px-3 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-accent"
          />
        </div>

        {filteredPlayers.length > 0 && (
          <div className="flex items-center justify-end px-4 py-2 border-b border-text-primary/10">
            <span className="text-xs font-semibold text-text-primary">Total Recs</span>
          </div>
        )}

        {!filteredPlayers.length ? (
          <div className="px-4 py-6 text-center text-xs text-text-muted">
            {!players?.length ? 'No pass catchers available yet — player data syncs after games are played.' : 'No players match your search.'}
          </div>
        ) : (
          <div className="max-h-[50vh] overflow-y-auto">
            {filteredPlayers.map((player) => {
              const isExhausted = usedPlayerIds.has(player.sleeper_player_id) && !thisWeekPickIds.has(player.sleeper_player_id)
              // Locked players stay in the list, greyed in place rather than
              // sunk or removed — seeing who leads the stat is the point, and
              // they keep their sorted position.
              const isLocked = !!player.is_locked
              return (
              <div
                key={player.sleeper_player_id}
                role="button"
                tabIndex={0}
                onClick={() => setDetailPlayer(player)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setDetailPlayer(player) }}
                className={`flex items-center gap-3 px-4 py-2.5 border-b border-text-primary/10 last:border-b-0 transition-colors cursor-pointer ${
                  isExhausted || isLocked ? 'opacity-40' : 'hover:bg-text-primary/5'
                }`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Shared component so a headshot that 404s falls back to initials
                      instead of vanishing — the inline version only had a
                      fallback when the URL was ABSENT, so a broken URL left
                      the row with no avatar at all. */}
                  <PlayerHeadshot name={player.full_name} url={player.headshot_url} size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-sm font-bold text-text-primary truncate">{player.player_name}</span>
                      {isLocked && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-text-muted/20 text-text-muted shrink-0">Started</span>
                      )}
                      <InjuryBadge status={player.injury_status} />
                    </div>
                    <div className="text-xs text-text-muted">
                      {player.position} · <span className="text-white">{player.team}</span>{player.opponent ? ` ${player.home_away === 'home' ? 'vs' : '@'} ${player.opponent}` : ''}
                      {isExhausted && (() => {
                        const u = usageById.get(player.sleeper_player_id)
                        return <span className="ml-1">· Used {u?.uses ?? '?'}/{u?.max_uses ?? '?'} this season</span>
                      })()}
                    </div>
                  </div>
                  <span className="font-display text-base text-white whitespace-nowrap shrink-0">{player.season_receptions || 0}</span>
                </div>
                {(!hasSavedPicks || editing) && (
                  <button
                    onClick={(e) => { e.stopPropagation(); addPlayer(player) }}
                    disabled={selected.length >= 3 || isExhausted || isLocked}
                    className="w-8 h-8 rounded-full border border-accent/40 text-accent hover:bg-accent hover:text-white transition-colors flex items-center justify-center shrink-0 text-lg font-bold leading-none disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    +
                  </button>
                )}
              </div>
              )
            })}
          </div>
        )}
      </div>

      {myHistory.length > 0 && (
        <div className="order-3 lg:col-start-1 lg:row-start-2 rounded-xl border border-text-primary/15 bg-bg-primary/15 backdrop-blur-md overflow-hidden mt-4 lg:mt-0">
          <button
            onClick={() => setHistoryOpen((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 border-b border-text-primary/10 hover:bg-text-primary/5 transition-colors"
          >
            <h3 className="text-sm font-semibold text-text-primary">Pick History</h3>
            <svg className={`w-4 h-4 text-text-muted shrink-0 transition-transform ${historyOpen ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {historyOpen && (
            <div className="divide-y divide-text-primary/10">
              {myHistory.map(({ week: w, picks }) => (
                <div key={w} className="px-4 py-3">
                  <div className="text-[10px] text-text-muted uppercase tracking-wider mb-2">Week {w}</div>
                  <div className="space-y-1.5">
                    {picks.map((pick, i) => (
                      <div key={i} className="flex items-center gap-2 bg-bg-primary/10 border border-text-primary/10 rounded-lg px-2.5 py-2">
                        <PlayerHeadshot name={pick.player_name} url={pick.headshot_url} size="sm" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-bold text-text-primary truncate">{pick.player_name}</div>
                          <div className="text-[10px] text-text-muted">{pick.position} · {pick.team}</div>
                        </div>
                        <span className={`font-display text-sm shrink-0 ${pick.receptions > 0 ? 'text-correct' : 'text-text-muted'}`}>{pick.receptions}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {detailPlayer && (
        <PlayerDetailModal player={detailPlayer} onClose={() => setDetailPlayer(null)} sport="americanfootball_nfl" />
      )}
    </div>
  )
}
